import "phaser";

import * as collision from "./collision";
import * as constants from "./constants";
import * as platform from "./platform";
import AudioEngine from "./audio_engine";

const LAUNCH_VELOCITY = 450;
const HANG_TIME = 45;
const WALK_SPEED = 220;

export class Player {
    game: Phaser.Game;
    player: Phaser.Sprite;
    cursors: Phaser.CursorKeys;

    tryJump: boolean;
    jumpKey: string | null = null;
    jumpTime = 0;
    dead = false;
    physicsPaused = false;
    overridePhysicsPaused = false;

    yAxis = p2.vec2.fromValues(0, 1);
    status: "normal" | "jumping";

    setupCollisions: (p:Player) => void;

    constructor(game: Phaser.Game, json: any,
                setupCollisions: (p:Player) => void, levelScale: number = 1) {
        let x = json.x * levelScale, y = json.y * levelScale;

        this.game = game;
        this.setupCollisions = setupCollisions;
        this.cursors = this.game.input.keyboard.createCursorKeys();

        this.tryJump = false;

        this.player = this.game.add.sprite(x, y, "ninja-spritesheet");
        this.player.width = 33;
        this.player.height = 48;
        this.player.animations.add("stand", [0, 1, 2, 3, 4, 5], 10);
        this.player.animations.add("walk", [6, 7, 8, 9], 10);
        this.player.animations.add("jump", [12, 13, 14], 10, false);
        this.player.animations.add("die", [18, 19], 10, false);
        this.player.anchor.setTo(0.5, 0.5);
        this.player.pivot.set(1., 0.6);

        this.game.physics.p2.enable(this.player);
        this.player.body.setRectangle(
            0.53 * this.player.width,
            this.player.height,
            0.21 * this.player.width,
        );
        this.setupCollisions(this);

        // only want to collide with left and right so this must be customized
        this.player.body.collideWorldBounds = false;

        this.player.body.fixedRotation = true;
        // without extra weight, the character slides off of platforms...
        this.player.body.mass = 1000;

        // Give the player velocity if encoded in the json
        if (json.vx) this.body.velocity.x = json.vx;
        if (json.vy) this.body.velocity.y = json.vy;
    }

    get body(): Phaser.Physics.P2.Body {
        return this.player.body;
    }

    get displayObject(): PIXI.DisplayObject {
        return this.player;
    }

    isDead(): boolean {
        return this.player.y > this.game.height || this.dead;
    }

    playDeathAnimation(callback?: () => void) {
        this.dead = true;
        this.player.animations.currentAnim.stop();
        this.player.animations.play("die");
        this.player.body.velocity.x = 0;
        this.player.body.velocity.y = -300;
        this.player.body.clearCollision();
        this.overridePhysicsPaused = true;
        this.game.time.events.add(300, () => {
            this.physicsPaused = true;

            this.game.time.events.add(300, () => {
                this.physicsPaused = false;
                this.player.body.velocity.x = 0;
                if (callback) callback();
            });

        });
    }

    undoDeath() {
        this.dead = false;
        this.setupCollisions(this);
        this.overridePhysicsPaused = false;
        this.physicsPaused = false;
        this.player.animations.play("stand");
    }

    updateBBox() {
        if (this.player.scale.x > 0) {
            this.player.anchor.setTo(0.5, 0.5);
        }
        else {
            this.player.anchor.setTo(1.0, 0.5);
        }
    }

    // TODO: Rename as "anyKeyPressed" lol
    jumpKeyPressed() {
        // The enemy's gate is down
        return this.cursors.up.isDown ||
            this.game.input.keyboard.isDown(Phaser.KeyCode.W) ||
            this.game.input.keyboard.isDown(Phaser.KeyCode.SPACEBAR);
    }

    emote(animation: string) {
        if (this.dead) return;
        this.player.animations.play(animation);
    }

    update(isCutting: boolean, isComplete: boolean,
           onJumpCallback: (key: string | null) => void) {
        if (this.isDead()) return;

        if (!isCutting) {
            this.player.body.velocity.x = 0;

            // We received a jump button press, jump if we are able to
            if(this.tryJump && this.canJump() && this.jumpTime === 0) {
                this.jumpTime = HANG_TIME;
                this.player.body.velocity.y = -LAUNCH_VELOCITY;
                this.player.animations.currentAnim.stop();
                this.emote("jump");
                this.status = "jumping";

                onJumpCallback(this.jumpKey);
                this.jumpKey = null;
            }
            this.tryJump = false;

            // P2 handles acceleration due to gravity. This just allows the player to
            // make shorter jumps if they don't press the up key for that long.
            if (!this.canJump() && this.jumpKeyPressed() && this.jumpTime > 0) {
                this.player.body.velocity.y -= 35;
            }

            // Decay our jump time, even if we are not using it
            this.jumpTime = Math.max(0, this.jumpTime - 5);

            // note: orig speed was 150
            // Horizontal movement
            if ((this.cursors.left.isDown ||
                this.game.input.keyboard.isDown(Phaser.KeyCode.A))
                && this.player.x > 0 && !isComplete) {
                this.player.body.velocity.x = -WALK_SPEED;
            }
            else if ((this.cursors.right.isDown ||
                     this.game.input.keyboard.isDown(Phaser.KeyCode.D)) &&
                     this.player.x < this.game.width && !isComplete) {
                this.player.body.velocity.x = WALK_SPEED;
            }
        }

        if (this.player.body.velocity.x < 0.0) {
            this.player.scale.x = -1;
        }
        else if (this.player.body.velocity.x > 0.0) {
            this.player.scale.x = 1;
        }

        if (Math.abs(this.player.body.velocity.x) > constants.FLOAT_EPSILON && this.canJump() && !this.tryJump) {
            AudioEngine.get().play("walking", false);
        }
        else {
            AudioEngine.get().stop("walking");
        }

        if (this.status === "normal") {
            if (this.player.body.velocity.x !== 0.0 &&
                this.canJump()) {
                this.emote("walk");
            }
            else if (this.canJump()) {
                this.player.animations.stop("walk", true);
                this.emote("stand");
            }
        }
        this.updateBBox();
    }

    // Uses phaser's built in collision detection to calculate if the player just landed
    // on a platform or not (as opposed to hitting from the bottom/side)
    // Source: https://phaser.io/examples/v2/p2-physics/platformer-material
    canJump() {
        return this.getCurrentPlatformData() !== undefined;
    }

    getCurrentPlatformFrom(platforms: Array<platform.Platform>): platform.Platform | undefined {
        let plat = this.getCurrentPlatformData();

        if(plat) {
            for (const p of platforms){
                if (p.physicsBody.data === plat) {
                    return p;
                }
            }
        }

        return undefined;
    }

    getCurrentPlatformData(): p2.Body | undefined {
        for (let i = 0; i < this.game.physics.p2.world.narrowphase.contactEquations.length; i++) {
            const c = this.game.physics.p2.world.narrowphase.contactEquations[i];
            if (c.bodyA === this.player.body.data || c.bodyB === this.player.body.data) {
                let d = p2.vec2.dot(c.normalA, this.yAxis);
                if (c.bodyA === this.player.body.data) {
                    d *= -1;
                }
                if (d > 0.5) {
                    return c.bodyA === this.player.body.data ? c.bodyB : c.bodyA;
                }
            }
        }

        return undefined;
    }

    onHitPlatform() {
        if (this.canJump()) {
            this.status = "normal";
            this.emote("stand");
            AudioEngine.get().play("hit_ground");
        }
    }

    translate(dx: number, dy: number): void {
        this.player.body.x += dx;
        this.player.body.y += dy;
    }

    setPosition(x: number, y: number): void {
        this.player.body.x = x;
        this.player.body.y = y;
    }

    get aabb(): collision.AABBLike {
        return { x: this.player.body.x,
                 y: this.player.body.y - this.player.height / 2,
                 width: Math.abs(0.53 * this.player.width),
                 height: this.player.height };
    }

    constrainTranslation(
        offsetX: number, offsetY: number,
        dx: number, dy: number,
        bbox: collision.AABBLike,
    ): [number, collision.ConstraintDirection[]] {
        let aabb = this.aabb;
        aabb.x += offsetX;
        aabb.y += offsetY;
        return collision.constrainAABBTranslation(
            aabb,
            dx, dy,
            bbox
        );
    }

    intersects(line: collision.Line): boolean {
        return line.intersectsAABB(this.aabb);
    }

    intersectionTime(line: collision.Line): number | null {
        return line.intersectsAABBTime(this.aabb);
    }

    whichSide(line: collision.Line): collision.LineSide {
        return line.whichSide(this.aabb);
    }

    highlightIntersection(highlight: boolean): void {
        this.player.tint = highlight ? 0xFF0000 : 0xFFFFFF;
    }

    serialize(): any {
        return {
            x: this.player.body.x,
            y: this.player.body.y,
            vx: this.body.velocity.x,
            vy: this.body.velocity.y,
        };
    }

    serializeMutable(): any {
        return {
            x: this.player.body.x,
            y: this.player.body.y,
            vx: this.body.velocity.x,
            vy: this.body.velocity.y,
        }
    }

    deserializeMutable(json: any, levelScale: number = 1): void {
        this.setPosition(json.x * levelScale, json.y * levelScale);
        this.body.velocity.x = json.vx || 0;
        this.body.velocity.y = json.vy || 0;
    }
}



// WEBPACK FOOTER //
// ./src/player.ts